import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useOrgWidgetData from '../hooks/useOrgWidgetData';
import OrgSiteRow from './OrgSiteRow';
import OrgWidgetFrame from './OrgWidgetFrame';
import {
  Content,
  Meta,
  OrgSummary,
  ScrollArea,
  SecondaryValue,
  ShareBar,
  ShareFill,
  SiteList,
  SiteValue,
  SummaryHeader,
  SummaryLabel,
  SummaryValue,
  EmptyText,
  ZeroToggle
} from './OrgWidgetLayout';
import { OrgWidgetDataState } from './OrgWidgetStates';
import { getOrgWidgetTitle, useResolveOrgWidgetMeta } from './orgWidgetMeta';

const defaultFormatValue = value => (value != null ? value : 0);

const OrgShareBySiteWidget = ({
  organisationId,
  widgetsMetadata,
  title,
  widgetName,
  emptyMessageKey,
  emptyMessageDefault,
  noSitesMessageKey,
  noSitesMessageDefault,
  summaryLabelKey,
  summaryLabelDefault,
  getSummaryValue,
  getSiteValue,
  getSharePct,
  shouldShowSharePct,
  renderSummaryMeta,
  formatSummaryValue,
  formatSiteValue,
  barColor,
  siteLinkTo,
  compact,
  loadingHeight,
  requireSites,
  barSize
}) => {
  const { t } = useTranslation();
  const [showZeros, setShowZeros] = useState(false);
  const meta = useResolveOrgWidgetMeta(widgetsMetadata, widgetName);
  const widgetTitle = getOrgWidgetTitle(title, meta);
  const { data, loading, error, reload } = useOrgWidgetData(organisationId, meta);

  const orgSummary = data && data.org_summary;
  const sites = (data && data.sites) || [];
  const formatSummary = formatSummaryValue || defaultFormatValue;
  const formatSite = formatSiteValue || defaultFormatValue;

  const isEmpty = !data || (requireSites && !sites.length);

  // Collapse sites with a zero value into one muted, toggleable row — unless
  // every site is zero, in which case list them all rather than hide the lot.
  const zeroCount = sites.filter(site => !getSiteValue(site)).length;
  const canCollapseZeros = zeroCount > 0 && zeroCount < sites.length;
  const visibleSites = (canCollapseZeros && !showZeros)
    ? sites.filter(site => getSiteValue(site))
    : sites;

  const renderEmptySites = () => {
    if (!noSitesMessageKey) return null;
    return (
      <EmptyText compact={compact}>
        {t(noSitesMessageKey, noSitesMessageDefault)}
      </EmptyText>
    );
  };

  return (
    <OrgWidgetDataState
      title={widgetTitle}
      loading={loading}
      hasError={error}
      onRetry={reload}
      isEmpty={isEmpty}
      loadingHeight={loadingHeight}
      compact={compact}
      emptyMessage={t(emptyMessageKey, emptyMessageDefault)}
    >
      <OrgWidgetFrame title={widgetTitle}>
        <ScrollArea>
          <Content compact={compact}>
            {orgSummary && (
              <OrgSummary compact={compact}>
                <SummaryHeader compact={compact}>
                  <SummaryLabel compact={compact}>
                    {t(summaryLabelKey, summaryLabelDefault)}
                  </SummaryLabel>
                  <SummaryValue compact={compact}>
                    {formatSummary(getSummaryValue(orgSummary))}
                  </SummaryValue>
                </SummaryHeader>
                {renderSummaryMeta ? (
                  <Meta compact={compact}>{renderSummaryMeta(t, orgSummary)}</Meta>
                ) : null}
              </OrgSummary>
            )}

            <SiteList compact={compact}>
              {sites.length ? visibleSites.map((site) => {
                const sharePct = getSharePct(site);
                const showShare = shouldShowSharePct(orgSummary, site);

                return (
                  <OrgSiteRow
                    key={site.account_id || site.name}
                    accountId={site.account_id}
                    name={site.name}
                    compact={compact}
                    to={siteLinkTo}
                  >
                    <ShareBar inRow compact={compact} medium={barSize === 'medium'}>
                      <ShareFill
                        pct={sharePct != null ? sharePct : 0}
                        color={barColor}
                      />
                    </ShareBar>
                    <SiteValue compact={compact}>
                      {formatSite(getSiteValue(site))}
                      {showShare && (
                        <SecondaryValue>
                          {` · ${sharePct != null ? sharePct : 0}%`}
                        </SecondaryValue>
                      )}
                    </SiteValue>
                  </OrgSiteRow>
                );
              }) : renderEmptySites()}
              {canCollapseZeros && (
                <ZeroToggle
                  compact={compact}
                  type="button"
                  onClick={() => setShowZeros(prev => !prev)}
                >
                  {showZeros
                    ? t('orgView.showLess', 'Show less')
                    : t('orgView.sitesWithNone', { count: zeroCount, defaultValue: '+{{count}} sites with none' })}
                </ZeroToggle>
              )}
            </SiteList>
          </Content>
        </ScrollArea>
      </OrgWidgetFrame>
    </OrgWidgetDataState>
  );
};

OrgShareBySiteWidget.propTypes = {
  organisationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  widgetsMetadata: PropTypes.object,
  title: PropTypes.string,
  widgetName: PropTypes.string.isRequired,
  emptyMessageKey: PropTypes.string.isRequired,
  emptyMessageDefault: PropTypes.string.isRequired,
  noSitesMessageKey: PropTypes.string,
  noSitesMessageDefault: PropTypes.string,
  summaryLabelKey: PropTypes.string.isRequired,
  summaryLabelDefault: PropTypes.string.isRequired,
  getSummaryValue: PropTypes.func.isRequired,
  getSiteValue: PropTypes.func.isRequired,
  getSharePct: PropTypes.func,
  shouldShowSharePct: PropTypes.func,
  renderSummaryMeta: PropTypes.func,
  formatSummaryValue: PropTypes.func,
  formatSiteValue: PropTypes.func,
  barColor: PropTypes.string.isRequired,
  // A path, or a builder run with the site's account id that may seed the
  // destination page's filters — see OrgSiteRow.
  siteLinkTo: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  compact: PropTypes.bool,
  loadingHeight: PropTypes.string,
  requireSites: PropTypes.bool,
  barSize: PropTypes.oneOf(['default', 'medium'])
};

OrgShareBySiteWidget.defaultProps = {
  title: null,
  widgetsMetadata: null,
  noSitesMessageKey: null,
  noSitesMessageDefault: null,
  renderSummaryMeta: null,
  formatSummaryValue: null,
  formatSiteValue: null,
  compact: false,
  loadingHeight: '120px',
  requireSites: true,
  shouldShowSharePct: () => false,
  getSharePct: site => site.share_pct,
  siteLinkTo: null,
  barSize: 'default'
};

export default OrgShareBySiteWidget;
